/**
 * Represents a task to be executed by the queue.
 * It's a function that returns a Promise of type T.
 */
export type AsyncTask<T> = () => Promise<T>;

/**
 * An asynchronous request queue with request deduplication and concurrency control.
 * Supports tasks with different return types in the same queue instance.
 */
export class RequestQueue {
  // --- Properties ---

  /**
   * Maximum number of requests to process concurrently.
   * @private
   */
  private readonly maxConcurrency: number;

  /**
   * The queue of tasks waiting to be processed. Each item contains the task,
   * its unique key, and the resolve/reject functions of its associated Promise.
   * @private
   */
  private queue: {
    key: string;
    task: AsyncTask<unknown>;
    resolve: (value: unknown) => void;
    reject: (reason?: unknown) => void;
  }[] = [];

  /**
   * A map to track requests that are currently in the queue or in-flight.
   * Key: A unique identifier for the request.
   * Value: The Promise that will resolve/reject when the request is complete.
   * This is the core of the deduplication mechanism.
   * @private
   */
  private pending: Map<string, Promise<unknown>> = new Map();

  /**
   * The current number of active (in-flight) requests.
   * @private
   */
  private activeRequests = 0;

  // --- Constructor ---

  /**
   * Creates an instance of the RequestQueue.
   * @param {number} [maxConcurrency=5] - The maximum number of concurrent requests.
   */
  constructor(maxConcurrency = 5) {
    if (maxConcurrency < 1) {
      throw new Error("Max concurrency must be at least 1.");
    }
    this.maxConcurrency = maxConcurrency;
  }

  // --- Public Methods ---

  /**
   * Adds a new asynchronous task to the queue.
   * If a task with the same key is already pending, it returns the existing promise.
   * @param {string} key - A unique key to identify and deduplicate the request.
   * @param {AsyncTask<T>} task - The asynchronous function to execute.
   * @returns {Promise<T>} A promise that resolves with the result of the task.
   */
  public add<T>(key: string, task: AsyncTask<T>): Promise<T> {
    // 1. Deduplication Check
    // If a request with this key is already pending, return its promise immediately.
    if (this.pending.has(key)) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return this.pending.get(key)! as Promise<T>;
    }

    // 2. Create a new Promise
    // This promise will be returned to the caller. We control its resolution
    // by storing its `resolve` and `reject` functions in our queue.
    const promise = new Promise<T>((resolve, reject) => {
      this.queue.push({
        key,
        task: task as AsyncTask<unknown>,
        resolve: resolve as (value: unknown) => void,
        reject,
      });
    });

    // 3. Track the new Promise for deduplication
    this.pending.set(key, promise);

    // 4. Trigger queue processing
    this.processQueue();

    return promise;
  }

  // --- Private Methods ---

  /**
   * Processes the queue, executing tasks as long as there are available
   * concurrency slots and items in the queue.
   * @private
   */
  private processQueue(): void {
    // Check if we can process another item.
    if (this.activeRequests >= this.maxConcurrency || this.queue.length === 0) {
      return;
    }

    // We have a free slot, so let's process the next item.
    this.activeRequests += 1;

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const item = this.queue.shift()!; // Take the next item from the front.

    // Execute the task.
    item
      .task()
      .then((value) => {
        // On success, resolve the promise we returned to the caller.
        item.resolve(value);
      })
      .catch((error) => {
        // On failure, reject the promise.
        item.reject(error);
      })
      .finally(() => {
        // This block runs regardless of success or failure.

        // 1. Clean up
        // The request is no longer pending, so remove it from the map.
        this.pending.delete(item.key);

        // 2. Free up a slot
        this.activeRequests -= 1;

        // 3. Try to process the next item
        // This is crucial! It creates a chain reaction to empty the queue.
        this.processQueue();
      });
  }
}
